import { MIN_PASSWORD_LENGTH } from "@dishour/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, unwrap } from "../api/client.js";
import { AppShell } from "../components/AppShell.js";
import { Field, buttonClass, inputClass } from "../components/Field.js";
import { Turnstile } from "../components/Turnstile.js";
import { fetchMe, homePathFor, sessionKey } from "../lib/session.js";

export function AcceptInvitePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const onToken = useCallback((t: string) => setCaptchaToken(t), []);

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const accept = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ password, turnstileToken: captchaToken }),
      });
      return unwrap<{ ok: true }>(res);
    },
    onSuccess: async () => {
      // Same fix as LoginPage: a cached `fetchQuery(sessionKey)` read can still be the
      // pre-acceptance `null`, which would send a freshly-activated account back to /login.
      const me = await fetchMe();
      queryClient.setQueryData(sessionKey, me);
      navigate(me ? homePathFor(me.role) : "/login", { replace: true });
    },
  });

  return (
    <AppShell title="비밀번호 설정">
      <p className="mb-4 text-stone-600">Dishour 계정의 비밀번호를 설정해 주세요.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          accept.mutate();
        }}
      >
        <Field
          label={`비밀번호 (${MIN_PASSWORD_LENGTH}자 이상)`}
          htmlFor="password"
          error={tooShort ? `${MIN_PASSWORD_LENGTH}자 이상 입력해 주세요.` : undefined}
        >
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            className={inputClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field
          label="비밀번호 확인"
          htmlFor="confirm"
          error={mismatch ? "비밀번호가 일치하지 않습니다." : undefined}
        >
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            className={inputClass}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <Turnstile onToken={onToken} />
        {accept.error ? (
          <p role="alert" className="mb-3 text-sm text-red-600">
            {accept.error instanceof ApiError ? accept.error.message : "처리에 실패했습니다."}
          </p>
        ) : null}
        <button
          type="submit"
          className={buttonClass}
          disabled={accept.isPending || mismatch || tooShort || !password || !captchaToken}
        >
          {accept.isPending ? "설정 중…" : "비밀번호 설정하고 시작하기"}
        </button>
      </form>
    </AppShell>
  );
}
